import { Component, Input, OnInit, ViewChild, inject } from "@angular/core";
import { ApiKeyModel } from "../../models/api-key.model";
import { ApiKeyService } from "../../services/api-key.service";
import { ManualPageType } from "@app/manual/models/manual-page-type";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { TabService } from "@services/tabs/TabService";
import { ApiKeyDetailComponent } from "../api-key-detail/api-key-detail.component";
import { firstValueFrom } from "rxjs";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/user/i18n/scope";

@Component({
    selector: "api-key-overview",
    templateUrl: "./api-key-overview.component.html",
    styleUrls: ["./api-key-overview.component.scss"],
    imports: [ApiKeyDetailComponent, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class ApiKeyOverviewComponent implements OnInit {
    private _ds = inject(ApiKeyService);
    private _prompt = inject(CerrixPromptService);
    private _tabService = inject(TabService);
    private _translation = injectTranslation(scope);

    @ViewChild(ApiKeyDetailComponent) editCmp: ApiKeyDetailComponent;
    @Input() userGuid: string = null;

    apiKeys: ApiKeyModel[];
    canEdit = false;
    selected: ApiKeyModel;

    async ngOnInit(): Promise<void> {
        this._ds.canEdit(this.userGuid).subscribe((canEdit) => {
            this.canEdit = canEdit;
        });
        this.loadData();
    }

    reset(): void {
        this.apiKeys = [];
    }

    loadData(): void {
        this.reset();
        this._ds.getApiKeys(this.userGuid).subscribe((x) => {
            this.apiKeys = x;
            if (this.selected && this.apiKeys.length > 0) {
                this.open(this.apiKeys.find((k) => k.id === this.selected.id));
            }
        });
    }

    async open(model: ApiKeyModel): Promise<void> {
        let data: ApiKeyModel;
        if (model.id > 0) {
            data = await firstValueFrom(this._ds.getApiKey(this.userGuid, model.id));
        } else {
            data = model;
        }

        this.selected = data;
        this.editCmp.open(data, !this.canEdit);
    }

    openManual(): void {
        this._tabService.openInternalManual(ManualPageType.ApiUsage);
    }

    async new(): Promise<void> {
        const template = await firstValueFrom(this._ds.getNew(this.userGuid));
        await this.open(template);
    }

    async save(): Promise<void> {
        if (!this.canEdit) {
            return;
        }

        await this._translation.ready();
        let loader = this._prompt.loader("", this._translation.t("apiKeys.savingApiKey"));

        try {
            const storeResult = await firstValueFrom(this._ds
                .storeApiKey(this.userGuid, this.selected));

            loader.close();
            loader = null;

            if (this.selected.id < 0) {
                this.selected.id = storeResult.id;
                this._prompt.alert({
                    maxHeight: "475px",
                    maxWidth: "600px",
                    data: {
                        title: this._translation.t("apiKeys.apiKeyCreatedTitle"),
                        message: this._translation.t("apiKeys.apiKeyCreatedMessage"),
                        closeOnEsc: false,
                        confirmOnEnter: false,
                        centerText: false,
                        fields: [
                            {
                                prettyName: this._translation.t("apiKeys.identifierField"),
                                fieldName: "identifier",
                                fieldType: GenericListFieldType.Text,
                                isReadonly: true,
                            },
                            {
                                prettyName: this._translation.t("apiKeys.passwordField"),
                                fieldName: "password",
                                fieldType: GenericListFieldType.Text,
                                isReadonly: true,
                            },
                        ],
                        fieldResult: storeResult,
                    },
                });
            }

            this.loadData();
        } catch (error) {
        } finally {
            if (loader) {
                loader.close();
            }
        }
    }

    async delete(): Promise<void> {
        if (!this.selected || this.selected.id <= 0) {
            return;
        }

        await this._translation.ready();

        const continueDelete = await this._prompt
            .confirmCustom({
                maxWidth: "400px",
                maxHeight: "250px",
                data: {
                    title: this._translation.t("apiKeys.deleteConfirmTitle"),
                    message: this._translation.t("apiKeys.deleteConfirmMessage"),
                },
            })
            .toPromise();

        if (continueDelete) {
            const loader = this._prompt.loader("", this._translation.t("apiKeys.deletingApiKey"));
            await firstValueFrom(this._ds.deleteApiKey(this.selected.id));

            this.selected = null;
            this.loadData();
            loader.close();
        }
    }
}
